import { useTranslation } from 'react-i18next';
import React from 'react';
import { SEO } from '../components/SEO';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Timer, CalendarRange } from 'lucide-react';
import ControlledGif from '../components/ControlledGif';
import SubjectsCarousel from '../components/SubjectsCarousel';
import ReviewsCarousel from '../components/ReviewsCarousel';
import GrowthChart from '../components/GrowthChart';
import useAuth from '../hooks/useAuth';
import api from '../utils/api';
import { WHATSAPP_UNIVERSITY_LINK } from '../constants/contact';
import gif from '../ui/LoopPrincipale.gif';
import piattaforma from '../ui/Piattaforma.gif';
import problemiGif from '../ui/Problemi.gif';
import zainoGif from '../ui/Zaino.gif';
import { ReactComponent as Blob1 } from '../ui/Blob1.svg';
import { ReactComponent as Blob3 } from '../ui/Blob3.svg';
import { ReactComponent as Blob4 } from '../ui/Blob4.svg';
import { ReactComponent as Blob5 } from '../ui/Blob5.svg';
import Piattaforma from '../ui/Piattaforma.png';
import ZainoPng from '../ui/Zaino.png';
import ProblemiPng from '../ui/Problemi.png';

// Valore mostrato se la chiamata ai prezzi non risponde
const FALLBACK_SINGLE_LESSON_PRICE = 21.5;
// Percorso formativo mostrato in home: statico, l'endpoint dei pacchetti richiede il login
const BUNDLE = { price: 120, n_hours: 6 };
const BUNDLE_HOURLY_PRICE = (BUNDLE.price / BUNDLE.n_hours).toFixed(0);

const PriceSkeleton = ({ className }: { className: string }) => (
  <div className={`animate-pulse bg-gray-200 rounded-lg mx-auto ${className}`} />
);

function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { auth } = useAuth();
  const [visibleSections, setVisibleSections] = useState<Set<number>>(new Set());
  const sectionRefs = useRef<(HTMLElement | null)[]>([]);
  const [singleLessonPrice, setSingleLessonPrice] = useState<number | null>(null);
  const [singlePriceLoading, setSinglePriceLoading] = useState(true);

  // Logica di navigazione condizionale
  useEffect(() => {
    if (process.env.REACT_APP_MAINTENANCE_MODE === 'true') return;
    if (auth.isFamily) {
      navigate('/family/home');
    } else if (auth.isTeacher) {
      navigate('/teacher/home');
    }
  }, [auth, navigate]);

  // Intersection Observer per animazioni fade-in
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = sectionRefs.current.indexOf(entry.target as HTMLElement);
          if (entry.isIntersecting && index !== -1) {
            setVisibleSections((prev) => new Set(prev).add(index));
          }
        });
      },
      { threshold: 0.1 },
    );

    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // Carica il prezzo della lezione singola dalla stessa API usata nel flusso di prenotazione.
  // skipAuthRedirect: la home è pubblica, un eventuale 401 non deve mandare al login.
  useEffect(() => {
    api
      .get('/lesson/price', { skipAuthRedirect: true })
      .then((response) => {
        const hourlyPrice = response.data.prices?.find((p: any) => Number(p.length) === 1);
        if (hourlyPrice) {
          setSingleLessonPrice(Number(hourlyPrice.price));
        }
      })
      .catch((error) => {
        console.error('Errore nel caricamento dei prezzi:', error);
      })
      .finally(() => {
        setSinglePriceLoading(false);
      });
  }, []);

  function registrationRedirect() {
    navigate('/register');
  }

  const scrollToContent = () => {
    const firstSection = sectionRefs.current[0];
    if (firstSection) {
      firstSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToSection = (index: number) => {
    const section = sectionRefs.current[index];
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const hourlyPrice = singleLessonPrice ?? FALLBACK_SINGLE_LESSON_PRICE;

  return (
    <>
      <SEO
        title="Ripetizioni per Tutti i Livelli Scolastici, Online e in Presenza | Formando PerCorsi"
        description="Ripetizioni con insegnanti selezionati per elementari, medie, superiori e università. Lezioni online, a domicilio o in sede. Prenota in pochi click con Formando PerCorsi."
        canonical="https://formandopercorsi.com/"
      />
      <main className="flex flex-col w-full overflow-x-hidden">
        {/* Hero section - Full viewport */}
        <section
          className="relative flex flex-col justify-center items-center md:min-h-screen bg-fpcwhite px-4 lg:px-16 md:pb-20"
          aria-label="Hero">
          <div className="flex flex-col sm:justify-between sm:flex-row p-2 sm:p-4 max-w-7xl w-full mx-auto flex-grow items-center gap-2 sm:gap-4">
            {/*sezione testo*/}
            <div className="flex flex-col gap-y-2 md:gap-y-4 md:justify-center md:items-center">
              <div className="flex flex-col">
                <h1 className="text-5xl lg:text-6xl font-bold text-fpcred pb-2 md:pb-4 text-center">
                  Ripetizioni
                  <span className="text-black"> per tutti i livelli di istruzione</span>
                </h1>
                <h2 className="text-xl md:text-3xl text-center px-2 sm:px-16 md:mt-8 font-semibold text-fpcred">
                  Online e in presenza!
                </h2>

                {/* <h3 className="text-lg md:text-xl text-black text-center px-2 sm:px-16 mt-4 md:mt-8">
                  Insegnanti selezionati per garantirti la massima qualità disponibili in pochissimi click!
                </h3> */}
              </div>

              {/*sezione LoopPrincipale*/}
              <div className="relative items-center justify-center flex flex-col sm:flex w-full max-w-xs sm:max-w-md mx-auto md:hidden">
                {/* <Blob1 className="w-full h-full max-w-full" /> */}
                <div className="flex items-center justify-center z-10 inset-0">
                  <img src={gif} alt="gif" className="max-w-full max-h-full object-contain" />
                </div>
              </div>

              <div className="flex justify-center items-center p-2 md:p-4">
                <div className="relative inline-block">
                  <button
                    className="px-4 py-3 md:p-4 rounded-full shadow-xl bg-fpcred transition-transform duration-300 ease-in-out hover:scale-110"
                    onClick={registrationRedirect}>
                    <span className="text-white font-semibold text-2xl md:text-xl">Prenota la lezione</span>
                  </button>
                  {/* Sticker promozionale */}
                  <div className="sticker-pulse absolute -top-12 -left-24 md:-top-4 md:left-auto md:-right-36 z-20 pointer-events-none">
                    <div className="bg-yellow-300 border-[2px] border-black rounded-lg px-2 py-1 md:px-3 md:py-1.5" style={{ boxShadow: '4px 4px 12px rgba(0, 0, 0, 0.5)' }}>
                      <span className="text-black font-extrabold text-lg leading-tight text-center whitespace-nowrap">
                        PROVA <span className="text-fpcred font-extrabold text-lg leading-tight text-center whitespace-nowrap">GRATUITA</span>
                        <br />
                        <span className="text-black justify-center flex font-extrabold text-lg leading-tight text-center whitespace-nowrap">
                          1ª ORA ONLINE
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <p className="text-center text-base md:text-xl text-black">
                  <span className=" text-black text-lg">
                    Hai dubbi? <span className="text-fpcred font-semibold">chiamaci!</span>
                  </span>
                  <br />
                  <a href="tel:3402817105" className="font-semibold underline text-lg">
                    340 281 7105
                  </a>
                </p>
              </div>
              {/* <div className="p-2 md:p-4 w-full flex justify-center gap-6 md:gap-8">
                <p className="font-bold text-black text-center">
                  <span className="text-2xl md:text-4xl">40</span>
                  <span className="text-2xl md:text-4xl text-fpcred">+</span>
                  <br />
                  <span className="text-lg">Insegnanti</span>
                </p>
                <p className="font-bold text-black text-center">
                  <span className="text-2xl md:text-4xl">10 000</span>
                  <span className="text-2xl md:text-4xl text-fpcred">+</span>
                  <br />
                  <span className="text-lg">lezioni all'anno</span>
                </p>
              </div> */}
            </div>
            {/*sezione LoopPrincipale*/}
            <div className="relative hidden items-center justify-center flex-col md:flex w-full max-w-xs sm:max-w-md mx-auto">
              <Blob1 className="w-full h-full max-w-full" />
              <div className="absolute flex items-center justify-center z-10 inset-0">
                <img src={gif} alt="gif" className="w-3/4 h-3/4 max-w-full max-h-full object-contain" />
              </div>
            </div>
          </div>

          {/* Freccia rimbalzante - dentro la hero section */}
          <div className="w-full flex justify-center md:mt-4">
            <button
              onClick={scrollToContent}
              className="animate-bounce cursor-pointer p-2 rounded-full hover:bg-fpcpink transition-colors"
              aria-label="Scorri verso il basso">
              <svg
                className="w-8 h-8 md:w-10 md:h-10 text-fpcred"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
              </svg>
            </button>
          </div>
        </section>

        {/* Sezione 1 - Affidati ai Professionisti */}
        <section
          ref={(el) => (sectionRefs.current[0] = el)}
          className="bg-fpcwhite py-8 md:py-16 px-4 lg:px-16 min-h-[85vh] md:min-h-screen flex flex-col"
          aria-label="Affidati ai Professionisti">
          <div className="flex-grow flex items-center w-full">
          <div className="max-w-7xl mx-auto w-full h-full">
            <div className="flex flex-col sm:justify-between gap-y-6 sm:gap-y-8 sm:flex-row items-center h-full">
              <div
                className={`flex flex-col items-center md:justify-center sm:w-1/2 order-1 sm:order-2 transition-all duration-1000 delay-200 ${visibleSections.has(0) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-black text-center mb-6 md:mb-12">
                  Affidati a chi lo fa <br />
                  <span className="text-fpcred"> per Professione</span>
                </h2>
                <div className="flex flex-col gap-4 px-4 text-center text-base md:text-xl lg:text-2xl text-black leading-relaxed">
                  <p
                    className={`transition-all duration-1000 delay-300 ${visibleSections.has(0) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                    <span className="font-semibold text-fpcred">Professionisti:</span> gli insegnanti di Formando
                    PerCorsi sono titolari di partita IVA, accuratamente selezionati.
                  </p>
                  <p
                    className={`transition-all duration-1000 delay-500 ${visibleSections.has(0) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                    <span className="font-semibold text-fpcred">Metodo di studio efficace:</span> I nostri insegnanti
                    non conoscono solo la materia, ma sanno come trasmetterla nel modo giusto.
                  </p>
                  <p
                    className={`transition-all duration-1000 delay-700 ${visibleSections.has(0) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                    <span className="font-semibold text-fpcred">Affiancamento continuo:</span> ogni studente è seguito
                    da un insegnante specializzato e dedicato per tutto il suo percorso.
                  </p>
                </div>
              </div>
              <div
                className={`w-full h-full items-center flex sm:w-1/2 order-2 sm:order-1 overflow-hidden transition-all duration-1000 delay-75 ${visibleSections.has(0) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                <ReviewsCarousel />
              </div>
            </div>
          </div>
          </div>

          {/* Freccia per sezione successiva */}
          <div className="w-full flex justify-center mt-auto pb-1 md:pb-2">
            <button
              onClick={() => document.getElementById('prezzi')?.scrollIntoView({ behavior: 'smooth' })}
              className="animate-bounce cursor-pointer p-2 rounded-full hover:bg-fpcpink transition-colors"
              aria-label="Scorri verso il basso">
              <svg
                className="w-8 h-8 md:w-10 md:h-10 text-fpcred"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
              </svg>
            </button>
          </div>
        </section>

        {/* Sezione Prezzi */}
        <section
          id="prezzi"
          className="bg-fpcwhite py-8 md:py-16 px-4 lg:px-16 min-h-[85vh] md:min-h-screen flex flex-col"
          aria-label="Prezzi">
          <div className="flex-grow flex items-center w-full">
          <div className="max-w-7xl mx-auto w-full">
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-black text-center mb-8 md:mb-12">
              Le nostre <span className="text-fpcred">Tariffe</span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {/* Card Lezione Singola */}
              <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl p-8 sm:p-12 lg:p-8 xl:p-10 border-2 border-fpcpink flex flex-col items-center text-center">
                <h3 className="text-xl sm:text-2xl font-bold text-black mb-4">Lezione Singola</h3>
                <p className="text-md text-gray-600 mb-6 min-h-[3.5rem] flex items-center">
                  Ideale per lezioni occasionali
                </p>
                <div className="text-5xl font-bold text-fpcred mb-8">
                  {singlePriceLoading ? (
                    <PriceSkeleton className="h-12 w-32" />
                  ) : (
                    <>
                      €{hourlyPrice}
                      <span className="text-lg sm:text-xl font-normal text-gray-500">/h</span>
                    </>
                  )}
                </div>
                <div className="mt-auto w-full">
                  <button
                    onClick={registrationRedirect}
                    className="w-full bg-fpcred text-white py-3 rounded-xl font-semibold text-base lg:text-lg hover:bg-buttonhover transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl">
                    Prenota Lezione Singola
                  </button>
                </div>
              </div>

              {/* Card Pacchetto 6 ore */}
              <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl p-8 sm:p-12 lg:p-8 xl:p-10 border-2 border-fpcpink flex flex-col items-center text-center">
                <h3 className="text-xl sm:text-2xl font-bold text-black mb-4">Percorso Formativo</h3>
                <p className="text-md text-gray-600 mb-6 min-h-[3.5rem] flex items-center">
                  {BUNDLE.n_hours} ore di lezione per preparare le verifiche al meglio!
                </p>
                <div className="text-5xl font-bold text-fpcred mb-1">€{BUNDLE.price}</div>
                <div className="text-sm sm:text-base text-gray-500 mb-8">€{BUNDLE_HOURLY_PRICE}/h</div>
                <div className="mt-auto w-full">
                  <button
                    onClick={registrationRedirect}
                    className="w-full bg-fpcred text-white py-3 rounded-xl font-semibold text-base lg:text-lg hover:bg-buttonhover transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl">
                    Prenota Percorso Formativo
                  </button>
                </div>
              </div>

              {/* Card Lezioni Universitarie */}
              <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl p-8 sm:p-12 lg:p-8 xl:p-10 border-2 border-fpcpink flex flex-col items-center text-center">
                <h3 className="text-xl sm:text-2xl font-bold text-black mb-4">Lezioni universitarie</h3>
                <p className="text-md text-gray-600 mb-6 min-h-[3.5rem] flex items-center">
                  Ripetizioni e supporto per gli esami universitari
                </p>
                <div className="text-5xl font-bold text-fpcred mb-8">
                  €25<span className="text-lg sm:text-xl font-normal text-gray-500">/h</span>
                </div>
                <div className="mt-auto w-full">
                  <a
                    href={WHATSAPP_UNIVERSITY_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Contattaci su WhatsApp per le lezioni universitarie"
                    className="block w-full bg-fpcred text-white py-3 rounded-xl font-semibold text-base lg:text-lg text-center hover:bg-buttonhover transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl">
                    Contattaci
                  </a>
                </div>
              </div>
            </div>
          </div>
          </div>

          {/* Freccia per sezione successiva */}
          <div className="w-full flex justify-center mt-12 md:mt-auto pb-1 md:pb-2">
            <button
              onClick={() => scrollToSection(1)}
              className="animate-bounce cursor-pointer p-2 rounded-full hover:bg-fpcpink transition-colors"
              aria-label="Scorri verso il basso">
              <svg
                className="w-8 h-8 md:w-10 md:h-10 text-fpcred"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
              </svg>
            </button>
          </div>
        </section>

        {/* Sezione 2 - Programma le lezioni */}
        <section
          ref={(el) => (sectionRefs.current[1] = el)}
          className="bg-fpcwhite py-8 md:py-16 px-4 lg:px-16 min-h-[85vh] md:min-h-screen flex flex-col"
          aria-label="Programma le lezioni">
          <div className="flex-grow flex items-center w-full">
          <div
            className={`max-w-7xl mx-auto w-full transition-all duration-1000 ${
              visibleSections.has(1) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}>
            <div className="flex flex-col sm:justify-between gap-y-6 sm:gap-y-8 sm:flex-row items-center">
              <div className="flex flex-col items-center md:justify-center sm:w-1/2 order-2 sm:order-1">
                <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-black text-center mb-8 md:mb-12">
                  <span className="text-fpcred">Programma</span> le lezioni, non farti trovare impreparato!
                </h2>
                <p className="text-center text-base md:text-xl lg:text-2xl text-black leading-relaxed">
                  La nostra piattaforma online è facile da usare e ti permette di prenotare lezioni in{' '}
                  <strong>pochi click</strong>, in modo rapido e sicuro. <br /> <br />
                  Non lasciare nulla al caso, programma in anticipo le lezioni per organizzare un percorso di studio che
                  porti a <span className="text-fpcred font-semibold">solidi miglioramenti scolastici!</span>
                </p>

                {/* Grafico crescente animato */}
                <GrowthChart isVisible={visibleSections.has(1)} />
              </div>
              <div className="relative items-center justify-center flex flex-col sm:flex w-full max-w-xs sm:max-w-md mx-auto sm:mx-0 order-1 sm:order-2">
                <Blob3 className="w-full h-full max-w-full" />
                <div className="absolute flex justify-center items-center z-10 inset-0">
                  <ControlledGif
                    gifSrc={piattaforma}
                    staticSrc={Piattaforma}
                    delay={400}
                    preload={true}
                    alt="piattaforma"
                    className="w-3/4 h-3/4 max-w-full max-h-full object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
          </div>

          {/* Freccia per sezione successiva */}
          <div className="w-full flex justify-center mt-auto pb-1 md:pb-2">
            <button
              onClick={() => scrollToSection(2)}
              className="animate-bounce cursor-pointer p-2 rounded-full hover:bg-fpcpink transition-colors"
              aria-label="Scorri verso il basso">
              <svg
                className="w-8 h-8 md:w-10 md:h-10 text-fpcred"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
              </svg>
            </button>
          </div>
        </section>

        {/* Sezione 3 - Percorsi personalizzati */}
        <section
          ref={(el) => (sectionRefs.current[2] = el)}
          className="bg-fpcwhite py-8 md:py-16 px-4 lg:px-16 min-h-screen flex flex-col"
          aria-label="Percorsi personalizzati">
          <div className="flex-grow flex items-center w-full">
          <div
            className={`max-w-7xl mx-auto w-full transition-all duration-1000 ${
              visibleSections.has(2) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}>
            <div className="flex flex-col sm:justify-between gap-y-6 sm:gap-y-8 sm:flex-row items-center">
              <div className="relative items-center justify-center flex flex-col sm:flex w-full max-w-xs sm:max-w-md mx-auto sm:mx-0">
                <Blob4 className="w-full h-full max-w-full" />
                <div className="absolute flex justify-center items-center z-10 inset-0">
                  <ControlledGif
                    gifSrc={zainoGif}
                    staticSrc={ZainoPng}
                    delay={400}
                    alt="zainoGif"
                    className="w-3/4 h-3/4 max-w-full max-h-full object-contain"
                  />
                </div>
              </div>
              <div className="flex flex-col items-center md:justify-center sm:w-1/2">
                <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-black text-center mb-6 md:mb-12">
                  Percorsi didattici <span className="text-fpcred">personalizzati</span>
                </h2>
                <p className="text-center text-base md:text-xl lg:text-2xl text-black leading-relaxed mb-4 md:mb-8">
                  Vuoi ottenere risultati duraturi che ti portino ad essere <strong>autonomo</strong> nello studio?
                  Grazie ai nostri percorsi didattici ti insegneremo un metodo di studio e tutte le conoscenze
                  necessarie per affrontare qualsiasi materia e livello scolastico.
                </p>

                {/* Pulsante per i prezzi */}
                <div className="flex flex-col items-center gap-2 mt-2 md:mt-4">
                  <button
                    className="px-4 py-3 md:p-4 rounded-full shadow-xl bg-fpcred transition-transform duration-300 ease-in-out hover:scale-110"
                    onClick={() => document.getElementById('prezzi')?.scrollIntoView({ behavior: 'smooth' })}>
                    <span className="text-white text-base md:text-xl">Scopri le offerte</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
          </div>

          {/* Freccia per sezione successiva */}
          <div className="w-full flex justify-center mt-auto pb-1 md:pb-2">
            <button
              onClick={() => scrollToSection(3)}
              className="animate-bounce cursor-pointer p-2 rounded-full hover:bg-fpcpink transition-colors"
              aria-label="Scorri verso il basso">
              <svg
                className="w-8 h-8 md:w-10 md:h-10 text-fpcred"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
              </svg>
            </button>
          </div>
        </section>

        {/* Sezione 4 - Team esperto */}
        <section
          ref={(el) => (sectionRefs.current[3] = el)}
          className="bg-fpcwhite py-8 md:py-16 px-4 lg:px-16 min-h-[85vh] md:min-h-screen flex flex-col"
          aria-label="Team esperto">
          <div className="flex-grow flex items-center w-full">
          <div
            className={`max-w-7xl mx-auto w-full transition-all duration-1000 ${
              visibleSections.has(3) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}>
            <div className="flex flex-col sm:justify-between gap-y-6 sm:gap-y-8 sm:flex-row items-center">
              <div className="flex flex-col items-center md:justify-center sm:w-1/2 order-2 sm:order-1">
                <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-black text-center mb-6 md:mb-12">
                  Affidati ad un team <span className="text-fpcred">esperto</span>
                </h2>
                <p className="text-center text-base md:text-xl lg:text-2xl text-black leading-relaxed">
                  Il nostro team ti permetterà di trovare una soluzione a qualsiasi problema tu debba affrontare. Siamo
                  qui per te, a tua disposizione per aiutarti a superare ogni ostacolo. Abbiamo esperienza decennale nel
                  mondo delle lezioni private e sappiamo come aiutare anche ragazzi con{' '}
                  <span className="text-fpcred font-semibold">BES</span> o{' '}
                  <span className="text-fpcred font-semibold">DSA</span>.
                </p>
                <div className="flex justify-center items-center p-2 md:p-4 mt-4">
                  <button
                    className="px-4 py-3 md:p-4 rounded-full shadow-xl bg-fpcred transition-transform duration-300 ease-in-out hover:scale-110"
                    onClick={registrationRedirect}>
                    <span className="text-white text-base md:text-xl">Prenota la tua lezione</span>
                  </button>
                </div>
              </div>
              <div className="relative items-center justify-center flex flex-col sm:flex w-full max-w-xs sm:max-w-md mx-auto sm:mx-0 order-1 sm:order-2">
                <Blob5 className="w-full h-full max-w-full" />
                <div className="absolute flex justify-center items-center z-10 inset-0">
                  <ControlledGif
                    gifSrc={problemiGif}
                    staticSrc={ProblemiPng}
                    delay={400}
                    alt="problemiGif"
                    className="w-3/4 h-3/4 max-w-full max-h-full object-contain"
                  />
                </div>
              </div>
            </div>

            {/* Carosello delle materie */}
            <div className="mt-8 md:mt-12 w-full">
              <SubjectsCarousel />
            </div>
          </div>
          </div>

          {/* Freccia per tornare all'inizio */}
          <div className="w-full flex justify-center mt-auto pb-1 md:pb-2">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="animate-bounce cursor-pointer p-2 rounded-full hover:bg-fpcpink transition-colors"
              aria-label="Torna su">
              <svg
                className="w-8 h-8 md:w-10 md:h-10 text-fpcred rotate-180"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
              </svg>
            </button>
          </div>
        </section>
      </main>
    </>
  );
}

export default Home;
